/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useCallback } from "react";
import {
	fetchAgentStatusList,
	fetchStatusAlertsList,
	clearStatusAlertChanges,
	updateStatusAlertChanges,
	updateStatusAlertAction,
	removeStatusAlertAction,
	addStatusAlertAction,
	saveRemoteStatusAlerts,
} from "./actions";
import { statusAlertsInitialState, statusAlertsReducer } from "./reducer";

const Context = React.createContext();

export const useStatusAlerts = () => React.useContext(Context);

export const StatusAlertsProvider = ({ children }) => {
	const [state, dispatch] = React.useReducer(statusAlertsReducer, statusAlertsInitialState);
	const { loaded } = state;

	const getAgentStatusList = useCallback(async () => {
		if (!loaded.statusLookup) {
			await fetchAgentStatusList(dispatch);
		}
	}, [loaded.statusLookup]);

	const getStatusAlertsList = useCallback(async () => {
		if (!loaded.statusAlerts) {
			await fetchStatusAlertsList(dispatch);
		}
	}, [loaded.statusAlerts]);

	const saveStatusAlertsList = useCallback(
		async (statusAlertId) => {
			const { statusAlerts } = state;
			const selectedStatusAlert = statusAlerts.find((status) => status.id === statusAlertId);
			if (selectedStatusAlert) {
				await saveRemoteStatusAlerts(dispatch, selectedStatusAlert);
			}
		},
		[state],
	);

	const clearStatusAlerts = useCallback(() => {
		clearStatusAlertChanges(dispatch);
	}, []);

	const updateStatusAlertsList = useCallback((statusAlerts) => {
		updateStatusAlertChanges(dispatch, statusAlerts);
	}, []);

	const updateSingleStatusAlert = useCallback((statusAlert) => {
		updateStatusAlertAction(dispatch, statusAlert);
	}, []);

	const removeStatusAlert = useCallback(async (statusAlertId) => {
		await removeStatusAlertAction(dispatch, statusAlertId);
	}, []);

	const addStatusAlert = useCallback((statusAlert) => {
		addStatusAlertAction(dispatch, statusAlert);
	}, []);

	return (
		<Context.Provider
			value={{
				...state,
				getAgentStatusList,
				getStatusAlertsList,
				saveStatusAlertsList,
				clearStatusAlerts,
				updateStatusAlertsList,
				updateSingleStatusAlert,
				removeStatusAlert,
				addStatusAlert,
			}}
		>
			{children}
		</Context.Provider>
	);
};
